// src/api/healthcare/ConsultationReportApi.ts

import { ENV } from '../../../config/runtimeEnv';
import { Patient } from "../../../types/healthcare/PatientTypes";
import { CompletedAppointment } from "../../../types/appointments/AppointmentTypes";
import { SpeakerMap, Transcript } from "../../../types/appointments/ConsultTypes";
import { ConsultChatMessage } from "../../../types/appointments/ConsultChatTypes";
import { ReportTemplate } from "../../../types/healthcare/ReportTypes";
import { Suggestion } from "../../../utils/healthcare/SugestionUtils";
import { generate, runKey } from "./ConsultGenerationApi";
import { fetchAppointmentSettings } from "../Appointment/AppointmentSettingsApi";
import type { UserUuid } from "../../../types/identity";
import { http } from "../../../lib/http/client";

/**
 * Saving a consultation — now THREE calls the browser makes itself.
 *
 * ═══ WHAT CHANGED ════════════════════════════════════════════════════════════════════════
 * `POST {talk}/api/generate-consultation-report` and `.../save-consultation-without-report`
 * DO NOT EXIST. `talk` used to generate, save, notify and finalise inside one endpoint; it
 * holds no AI at all now (ClickUp 86d3r82eh, M19/M25) and no longer performs the clinical
 * write. The browser owns the sequence:
 *
 *     1. GENERATE   healthcare  POST /practitioner/consult-generations   (pipeline consult_report)
 *     2. SAVE       healthcare  POST /practitioner/appointments/completed
 *     3. FINALISE   talk        POST /api/finalise-consult
 *
 * Only step 3 is still `talk`'s, and only because it is the part nothing else can do: it
 * holds the room's Redis state and is the only thing that can ask `transcribe` to
 * re-transcribe the buffered audio.
 *
 * ⚠️ `requestMobileMicToken` below still points at `talk`. That route survives — leave it.
 *
 * ═══ *** THE ORDER, AND WHAT EACH FAILURE COSTS *** ══════════════════════════════════════
 * The three steps are ordered by what is lost when each one fails, and the sequence is
 * strictly abort-on-failure until the consultation is durable:
 *
 *   1. **GENERATE first, and a failure ABORTS — nothing is written.** Generation is the slow,
 *      failure-prone step. Saving first would mean a failed generation leaves a row whose
 *      `report` column is blank, and healthcare's upsert protects a stored non-empty value
 *      but has nothing to protect on a first save. Generating first means the practitioner
 *      sees the failure against NO written row and can simply press the button again, or
 *      fall back to "save without a report".
 *   2. **SAVE second — this is the step that makes the consultation durable**, and a failure
 *      also aborts. Step 3 is deliberately NOT attempted: `finalise-consult` deletes
 *      `room:{id}:info` and `room:{id}:transcripts`, and those transcripts exist NOWHERE ELSE
 *      until the save lands. Tearing the room down behind a failed save destroys the only
 *      copy of the consultation.
 *   3. **FINALISE last, and a failure is SWALLOWED.** The consultation is already durable by
 *      the time this runs. The final pass is a quality improvement (a better transcript, the
 *      recording filed) and the room keys expire on their own 86400s TTL, so failing the
 *      practitioner's save because `talk` or `transcribe` is unreachable would trade a
 *      certainty for a nicety. It is logged and nothing else.
 *
 * ⚠️ **THE DEFECT THIS DELIBERATELY DOES NOT REPRODUCE.** The old chain had the browser time
 * out at 180s while `talk` ran on to 360s and **saved the appointment anyway** — so the
 * practitioner was shown a failure against a written row, and pressing the button again wrote
 * a second one. There is no server carrying on after this browser gives up any more: the
 * browser owns all three calls, and each budget below exceeds the whole of the hop beneath it.
 *
 * ⚠️ **THE PRACTITIONER NOTIFICATION IS HEALTHCARE'S NOW.** `CompletedAppointmentController`
 * fires it from inside `store()`, at most once per `app_id`, judged from the row's
 * before-state. Do not send one from here — the save is called at least twice per consult (the
 * browser, then the deferred final pass) and a second notification lands long after the
 * practitioner has moved on.
 */

const TALK_SERVER = (ENV.VITE_TALK_SERVER as string) || '/talk';
// talk is reached through the clinic gateway on this origin (contract §22); an absolute host would skip the session cookie.
if (TALK_SERVER.startsWith('http')) console.error('VITE_TALK_SERVER must be same-origin (/talk)');

/** ⚠️ ALREADY ENDS IN `/api` in every ConfigMap — hence `${BASE}/practitioner/...` and never
 *  `${BASE}/api/practitioner/...`. Same as every sibling module in this directory. */
const HEALTHCARE_SERVER = ENV.VITE_HEALTHCARE_SERVER;

/** Speaker label → who that is, the room whose live state the save may tear down, and the two
 *  ids the finalise hand-off needs.
 *
 *  All five are OPTIONAL at every hop, deliberately: a deployed frontend from before the
 *  pipeline upgrade sends none of them, and the consultation must still save — with an empty
 *  speaker map and the room's Redis keys left to their own TTL. */
interface ConsultSaveExtras {
	speakerMap?: SpeakerMap;
	roomId?: string;
	/** Carried into `finalise-consult`, which persists the deferred pass's context. talk falls
	 *  back to the room's own stored patient when this is absent. */
	practitionerId?: UserUuid;
	patientId?: UserUuid;
	/** The live consult chat. Declared HERE rather than on either request type so both save paths
	 *  archive it — talk deletes `room:{id}:chat` the moment the save succeeds. */
	chat?: ConsultChatMessage[];
}

export interface GenerateReportRequest extends ConsultSaveExtras {
	patient: Patient;
	consultation: CompletedAppointment;
	transcripts: Transcript[];
	template: ReportTemplate;
	consultGuidance?: any;
	suggestions: Suggestion[];
	practitionerId: UserUuid;
	/** The appointment's own start, so a consult finishing after midnight still dates to the day
	 *  it was booked for. Falls back to now, which is right for every ordinary save. */
	consultationAt?: Date | string | null;
}

export interface GenerateReportResponse {
	success: boolean;
	report: string;
	/** `completed_appointments.id`, from the 201 the save returns. */
	completedConsultationId: number;
}

export interface SaveConsultationRequest extends ConsultSaveExtras {
	consultation: CompletedAppointment;
	transcripts: Transcript[];
	suggestions: Suggestion[];
}

export interface SaveConsultationResponse {
	success: boolean;
	completedConsultationId: number;
}

/**
 * 300s for the generation.
 *
 *     280   `ConsultGenerationController::timeouts()['consult_report']`
 *   +  10   `services.xoe_agent.connect_timeout`
 *   = 290   healthcare's worst case at the relay
 *     300   this
 *
 * Widening this without moving the relay's 280 first only lengthens how long the practitioner
 * waits before the same failure; narrowing it below 290 recreates the old defect from the other
 * side — the browser giving up on work that is still running.
 */
const GENERATE_TIMEOUT_MS = 300000;

/** The save is one upsert plus a fire-and-forget notification. */
const SAVE_TIMEOUT_MS = 30000;

/** Best-effort cleanup. `finaliseConsultAfterSave` never throws and answers `{success:true}`
 *  regardless, so this bounds an unreachable talk rather than a slow one. */
const FINALISE_TIMEOUT_MS = 10000;

/** The clinic-timezone read that precedes the generation. Short on purpose — it degrades to the
 *  practitioner's own zone, so waiting on it is never worth delaying the report. */
const SETTINGS_TIMEOUT_MS = 5000;

/**
 * Step 2 — write the consultation into `completed_appointments`.
 *
 * ⚠️ **`transcript` AND `speaker_map` MUST BE JSON STRINGS.** Both are validated with Laravel's
 * `json` rule, which **REJECTS ARRAYS** — an array is a 422, and passing the objects through
 * would fail every save with a validation error that names a field the practitioner cannot see.
 * `transcript` is `required`, so it is always stringified even when empty (`"[]"`).
 *
 * Healthcare's `upsertPayload()` never blanks a stored non-empty column, and `isEmptyValue()`
 * decodes JSON first — so `"[]"`, `"{}"` and `""` are all treated as "no content" and cannot
 * overwrite a populated one. That is what makes a re-POST of the same `app_id` safe, which the
 * deferred final pass relies on minutes later.
 *
 * `transcript_status: 'live'` is this browser's claim about its own copy. The final pass
 * re-POSTs the same row as `'final'` once it has re-transcribed the buffered audio.
 */
const saveCompletedConsultation = async (
	request: SaveConsultationRequest,
	report: string
): Promise<number> => {
	const response = await http.post(
		`${HEALTHCARE_SERVER}/practitioner/appointments/completed`,
		{
			appointmentData: {
				app_id: request.consultation.app_id,
				transcript: JSON.stringify(request.transcripts ?? []),
				speaker_map: request.speakerMap ? JSON.stringify(request.speakerMap) : null,
				transcript_status: "live",
				audio_files: request.consultation.audio_files ?? "",
				report,
				notes: request.consultation.notes ?? "",
				vitals: request.consultation.vitals ?? "",
				diagnosis: request.consultation.diagnosis ?? "",
				suggestions: JSON.stringify(request.suggestions ?? []),
				// Stringified like `transcript` — the column is `nullable|json`, and an array is a 422.
				chat: JSON.stringify(request.chat ?? []),
			},
		},
		{
			timeout: SAVE_TIMEOUT_MS,
		}
	);

	// 201 with the model. `id` is `completed_appointments.id`.
	return response.data?.id;
};

/**
 * Step 3 — hand the room to the post-consult final pass and tear its live state down.
 *
 * No credential in the body (contract §20.6): talk stores attribution from the request assertion and runs the final pass in service mode.
 *
 * NEVER THROWS. The consultation is already durable before this is called.
 */
const finaliseConsult = async (request: ConsultSaveExtras & { appId: number; transcripts: Transcript[] }): Promise<void> => {
	try {
		await http.post(
			`${TALK_SERVER}/api/finalise-consult`,
			{
				roomId: request.roomId,
				appId: request.appId,
				transcripts: request.transcripts,
				practitionerId: request.practitionerId,
				patientId: request.patientId,
			},
			{
				timeout: FINALISE_TIMEOUT_MS,
			}
		);
	} catch (error) {
		// Swallowed on purpose — see the ordering note at the top of this file. The room keys
		// expire on their own TTL and the practitioner's save has already succeeded.
		// Status only: the axios error carries the request body, i.e. the transcript.
		console.error("consult.finalise.failed", { status: (error as { response?: { status?: number } })?.response?.status });
	}
};

/**
 * The zone the consultation's transcript timestamps are rendered in.
 *
 * ⚠️ **THE FALLBACK IS THIS BROWSER'S ZONE, NEVER UTC.** The agent falls back to UTC when the key
 * is absent, which is the correct behaviour for a frontend that predates it and the wrong one for
 * a practitioner whose settings call happened to fail — UTC is nobody's clock, and a UTC-stamped
 * report is the defect S17 exists to fix.
 *
 * Read off `company_appointment_settings.timezone` — the same singleton the appointment settings
 * screen writes, and the only clinic-wide zone on the platform.
 */
const resolveClinicTimezone = async (): Promise<string> => {
	const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	try {
		// ⚠️ BOUNDED HERE, because `fetchAppointmentSettings()` takes no timeout and every other
		// call in this sequence has one. Unbounded, an unreachable healthcare would hang the
		// practitioner's save in front of the generation rather than inside its own 300s budget.
		const settings = await Promise.race([
			fetchAppointmentSettings(),
			new Promise<null>((resolve) => setTimeout(() => resolve(null), SETTINGS_TIMEOUT_MS)),
		]);

		if (settings?.timezone) {
			return settings.timezone;
		}
	} catch (error) {
		console.error("Clinic timezone unavailable; using this browser's zone for the report:", error);
	}

	return browserZone;
};

/**
 * `YYYY-MM-DD` for `when` as the clinic's calendar reads it.
 *
 * Assembled from `formatToParts` rather than a locale string: every `en-*` locale orders the
 * fields differently and `en-CA`'s ISO shape is an ICU detail, not a guarantee. An unusable zone
 * yields `""`, which the agent renders as no date at all — a wrong date on a signed clinical
 * record is worse than an absent one.
 */
const clinicCalendarDate = (timeZone: string, when: Date): string => {
	if (Number.isNaN(when.getTime())) {
		return "";
	}

	try {
		const parts = new Intl.DateTimeFormat("en-GB", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(when);
		const value = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
		const [year, month, day] = [value("year"), value("month"), value("day")];

		return year && month && day ? `${year}-${month}-${day}` : "";
	} catch (error) {
		console.error("Could not date the consultation in the clinic's timezone:", error);
		return "";
	}
};

export const generateConsultationReport = async (
	request: GenerateReportRequest
): Promise<GenerateReportResponse> => {
	// ── 1. GENERATE ─────────────────────────────────────────────────────────────────────
	//
	// ⚠️ The `inputs` keys are `consult_report.py::build_user_message()`'s contract, read off
	// that builder. Note `consult_guidance`, `speaker_map`, `clinic_timezone` and
	// `consultation_date` are SNAKE_CASE while the frontend variables are camelCase — a mismatch
	// here does not error, it renders the prompt's default and the report silently loses a whole
	// data source the template's instructions explicitly reference. A mistyped `clinic_timezone`
	// is the worst of them: the agent falls back to UTC, nothing fails, and every transcript line
	// in the signed record is stamped with the wrong half of the day.
	//
	// `patient` goes AS RECEIVED. The 14-field narrowing (`PATIENT_SUMMARY_FIELDS`) lives
	// beside the prompt that consumes it, in the agent — deliberately, so there is one field
	// list rather than two in two repositories drifting apart.
	const clinicTimezone = await resolveClinicTimezone();
	const envelope = await generate<string>({
		pipeline: "consult_report",
		runKey: runKey(),
		patientId: request.patient.id,
		inputs: {
			patient: request.patient,
			consultation: request.consultation,
			transcripts: request.transcripts,
			template: request.template,
			consult_guidance: request.consultGuidance ?? null,
			suggestions: request.suggestions,
			speaker_map: request.speakerMap ?? {},
			clinic_timezone: clinicTimezone,
			consultation_date: clinicCalendarDate(clinicTimezone, new Date(request.consultationAt ?? Date.now())),
		},
		timeoutMs: GENERATE_TIMEOUT_MS,
	});

	// A 200 is not success. `data` is a BARE HTML STRING on this pipeline (the agent's
	// `parse()` unwraps `{report_html}` and refuses an empty or whitespace-only answer), and
	// it is `null` on every failure.
	const report = envelope.metadata?.status === "complete" ? envelope.data : null;

	if (typeof report !== "string" || report.trim() === "") {
		// ⚠️ ABORT BEFORE THE SAVE. A blank report written over a good one is unrecoverable
		// once the room's transcript keys are gone; a failure with nothing written is not.
		// `metadata.error` is a closed server-side vocabulary, never model text, so it is safe
		// to surface.
		const code = typeof envelope.metadata?.error === "string" ? envelope.metadata.error : "unknown";
		throw new Error(
			`The consultation report could not be generated (${code}). Nothing has been saved — try again, or save without a report.`
		);
	}

	// ── 2. SAVE ─────────────────────────────────────────────────────────────────────────
	// Throws on failure, and step 3 is then never reached — see the ordering note.
	const completedConsultationId = await saveCompletedConsultation(request, report);

	// ── 3. FINALISE ─────────────────────────────────────────────────────────────────────
	await finaliseConsult({
		roomId: request.roomId,
		appId: request.consultation.app_id,
		transcripts: request.transcripts,
		practitionerId: request.practitionerId,
		patientId: request.patientId ?? request.patient.id,
	});

	return { success: true, report, completedConsultationId };
};

export interface MobileMicTokenResponse {
	token: string;
	expiresAt: number;
}

/**
 * Mint the mobile microphone's credential for one room (S34).
 *
 * The practitioner's session reaches talk as a cookie through the gateway (contract §20). The
 * 15-minute token is short-lived by design — refetch on every QR modal open rather than caching it.
 */
export const requestMobileMicToken = async (roomId: string): Promise<MobileMicTokenResponse> => {
	const response = await http.post(
		`${TALK_SERVER}/api/mobile-mic-token`,
		{ roomId },
		{
			timeout: 10000
		}
	);

	return response.data;
};

/**
 * The same path minus step 1 — the practitioner opted out of a report, so there is nothing to
 * generate and `report` is `''`.
 *
 * ⚠️ `''` cannot blank a stored report: healthcare's `upsertPayload()` rejects an empty value
 * for a column that already holds one. So "save without a report" after a report has already
 * been generated for this `app_id` keeps the report, which is what the practitioner means.
 */
export const saveConsultationWithoutReport = async (
	request: SaveConsultationRequest
): Promise<SaveConsultationResponse> => {
	const completedConsultationId = await saveCompletedConsultation(request, "");

	await finaliseConsult({
		roomId: request.roomId,
		appId: request.consultation.app_id,
		transcripts: request.transcripts,
		practitionerId: request.practitionerId,
		patientId: request.patientId,
	});

	return { success: true, completedConsultationId };
};
